import React, { useState } from 'react';
import {
  Box,
  Input,
  VStack,
  HStack,
  Text,
  IconButton,
  Switch,
  Heading,
  FormControl,
  FormLabel,
  Tooltip,
  Button,
  Flex,
  useToast,
  Divider,
  Checkbox,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon, QuestionIcon } from '@chakra-ui/icons';
import { updateAppActiveStep } from '../../../redux/appBuilder/stepperDetails';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { getAppActiveStep } from '../../../redux/appBuilder/stepperDetails/selectors';
import { setSelectedNetworkRequest } from '../../../redux/providerBuilder/selectedNetworkRequest';
import { resetSelectedInstances } from '../../../redux/providerBuilder/selectedInstances';
import { getAppDetails } from '../../../redux/appBuilder/appDetails/selectors';
import { HiClipboard } from 'react-icons/hi';

interface HeaderPair {
  key: string;
  value: string;
  isPrivate: boolean;
}

interface BodyPair {
  key: string;
  value: string;
}

const RequestBuild = () => {
  const dispatch = useAppDispatch();
  const activeStep = useAppSelector(getAppActiveStep);
  const toast = useToast();
  const appDetails = useAppSelector(getAppDetails);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [copiedText, setCopiedText] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [url, setUrl] = useState('');
  const [createRequest, setCreateRequest] = useState(false);

  const [headers, setHeaders] = useState<HeaderPair[]>([
    { key: 'accept', value: 'application/json', isPrivate: false },
  ]);
  const [bodyPairs, setBodyPairs] = useState<BodyPair[]>([{ key: '', value: '' }]);

  const addHeader = () => {
    setHeaders([...headers, { key: '', value: '', isPrivate: false }]);
  };

  const addBodyPair = () => {
    setBodyPairs([...bodyPairs, { key: '', value: '' }]);
  };

  const removeHeader = (index: number) => {
    const newHeaders = headers.filter((_, idx) => idx !== index);
    setHeaders(newHeaders);
  };

  const removeBodyPair = (index: number) => {
    const newBodyPairs = bodyPairs.filter((_, idx) => idx !== index);
    setBodyPairs(newBodyPairs);
  };

  const updateHeader = (
    index: number,
    field: keyof HeaderPair,
    value: string | boolean
  ) => {
    const newHeaders = [...headers];
    newHeaders[index] = { ...newHeaders[index], [field]: value };
    setHeaders(newHeaders);
  };

  const updateBodyPair = (
    index: number,
    field: keyof BodyPair,
    value: string | boolean
  ) => {
    const newBodyPairs = [...bodyPairs];
    newBodyPairs[index] = { ...newBodyPairs[index], [field]: value };
    setBodyPairs(newBodyPairs);
  };
  // handleRequestClick
  const handelFetch = async (
    httpHeaders: { [key: string]: string },
    privateHttpHeaders: { [key: string]: string },
    body?: BodyPair
  ) => {
    const fetchOptions = {
      method: 'GET',
      headers: { ...httpHeaders, ...privateHttpHeaders },
      // body: body
    };
    try {
      const response = await fetch(url, fetchOptions);
      const data = await response.text();

      console.log('response', data);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new TypeError('Response is not JSON');
      }
      const stringData = JSON.parse(data);
      if (!stringData || Object.keys(stringData).length === 0) {
        throw new Error('Response body is empty');
      }
      return data;
    } catch (error) {
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        toast({
          title: 'Network Error',
          description: error.message,
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
        console.error('Network error:', error.message);
      } else {
        toast({
          title: 'Fetch Error',
          description: (error as Error).message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        console.error('Error:', (error as Error).message);
      }
    }
  };
  const handleCopy = (id: string | undefined) => {
    if (!id) return;
    navigator.clipboard.writeText(id);
    setCopiedText(id);
    toast({
      title: 'Provider ID Copied',
      description: 'The ID has been copied to the clipboard.',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };
  const handelNextClicked = async () => {
    let httpHeaders: { [key: string]: string } = {};
    let privateHttpHeaders: { [key: string]: string } = {};

    headers.map((e) => {
      if (e.key)
        e.isPrivate
          ? (privateHttpHeaders[e.key] = e.value)
          : (httpHeaders[e.key] = e.value);
    });
    try {
      const w = new URL(url);
    } catch {
      toast({
        title: 'URL not valid',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    setIsLoading(true);
    const data = await handelFetch(httpHeaders, privateHttpHeaders);
    if (!data) {
      setIsLoading(false);
      return;
    }
    console.log('response', data);
    const request = {
      url,
      requestType: 'GET',
      response: {
        body: data,
      },
      contentType: 'json',
      publicHeaders: httpHeaders,
      privateHeaders: privateHttpHeaders,
    };
    dispatch(setSelectedNetworkRequest([request]));
    dispatch(resetSelectedInstances());
    setIsLoading(false);
    dispatch(updateAppActiveStep(activeStep + 1));
  };

  const handleFinishClicked = () => {
    dispatch(updateAppActiveStep(5));
  };

  return (
    <Box className="w-full max-w-2xl p-6 bg-white rounded-lg shadow-lg">
      <HStack>
        <VStack alignItems="flex-start" w="500%" spacing={0}>
          <HStack>
            <Text
              color="gray"
              className="small-font"
              whiteSpace="pre-line"
              wordBreak="break-word"
              noOfLines={1}
              fontFamily="inter"
            >
              Application ID
            </Text>
            <IconButton
              size="sm"
              icon={<HiClipboard />}
              onClick={() => handleCopy(appDetails.appId)}
              aria-label="Copy App ID"
            />
          </HStack>
          <Text
            color="black.600"
            className="small-font"
            fontWeight="bold"
            w="100%"
            noOfLines={1}
            whiteSpace="pre-line"
            fontFamily="inter"
          >
            {appDetails.appId}
          </Text>
        </VStack>
        <VStack alignItems="flex-start" w="50%" spacing="0px">
          <HStack>
            <Text
              color="gray"
              className="small-font"
              whiteSpace="pre-line"
              wordBreak="break-word"
              noOfLines={1}
              fontFamily="inter"
            >
              Application Secret
            </Text>
            <IconButton
              size="sm"
              icon={<HiClipboard />}
              onClick={() => handleCopy(appDetails.privateKey)}
              aria-label="Copy Secret Key"
            />
          </HStack>
          <Text
            color="black.600"
            className="small-font"
            w="100%"
            whiteSpace="pre-line"
            fontWeight="bold"
            noOfLines={1}
            fontFamily="inter"
          >
            {appDetails.privateKey}
          </Text>
        </VStack>
      </HStack>
      <Divider paddingTop={4} marginBottom={4} borderBottomWidth={2} />
      <VStack spacing={2} align="stretch">
        <Box>
          <HStack>
            <Heading size="md" className="mb-4">
              Create a request
            </Heading>
            <Checkbox onChange={(e) => setCreateRequest(e.target.checked)} />
            <Text ml={2} color="gray.500" fontSize="sm">
              (Optional)
            </Text>
          </HStack>
          <Text
            color="gray"
            className="small-font"
            whiteSpace="pre-line"
            wordBreak="break-word"
            noOfLines={1}
            fontFamily="inter"
          >
            Construct the http request you want to extract data from
          </Text>
        </Box>

        {createRequest && (
          <>
            <FormControl>
              <FormLabel>URL</FormLabel>
              <Input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://api.reclaimprotocol/my-endpoint"
                className="w-full"
              />
            </FormControl>

            <Box>
              <HStack className="mb-4" justify="space-between">
                <HStack spacing={2}>
                  <Text fontWeight="bold">HTTP Headers</Text>
                  <Tooltip label="Add custom headers to your request, set data the cannot be revealed to private">
                    <IconButton
                      aria-label="Help"
                      icon={<QuestionIcon />}
                      variant="ghost"
                      size="sm"
                    />
                  </Tooltip>
                  <IconButton
                    aria-label="Add header"
                    icon={<AddIcon />}
                    onClick={addHeader}
                    variant="ghost"
                    size="sm"
                  />
                </HStack>
              </HStack>

              <VStack spacing={4}>
                {headers.map((header, index) => (
                  <HStack key={index} spacing={2} className="w-full">
                    <Input
                      value={header.key}
                      onChange={(e) => updateHeader(index, 'key', e.target.value)}
                      placeholder="Header key"
                      size="md"
                      variant="outline"
                      className="rounded-md border-gray-300"
                    />
                    <Input
                      value={header.value}
                      onChange={(e) => updateHeader(index, 'value', e.target.value)}
                      placeholder="Header value"
                      size="md"
                      variant="outline"
                      className="rounded-md border-gray-300"
                    />
                    <Switch
                      isChecked={header.isPrivate}
                      onChange={(e) => updateHeader(index, 'isPrivate', e.target.checked)}
                      size="md"
                    />
                    <Text className="text-sm text-gray-600">Private</Text>
                    <IconButton
                      aria-label="Delete header"
                      icon={<DeleteIcon color={'black'} />}
                      onClick={() => removeHeader(index)}
                      variant="ghost"
                      size="sm"
                      colorScheme="red"
                    />
                  </HStack>
                ))}
              </VStack>
            </Box>
          </>
        )}
        {/* <Box>
                    <HStack className="mb-4" justify="space-between">
                        <HStack spacing={2}>
                            <Text fontWeight="bold">HTTP Body</Text>
                            <Tooltip label="Add body parameters to your request">
                                <IconButton
                                    aria-label="Help"
                                    icon={<QuestionIcon />}
                                    variant="ghost"
                                    size="sm"
                                />
                            </Tooltip>
                            <IconButton
                                aria-label="Add body parameter"
                                icon={<AddIcon />}
                                onClick={addBodyPair}
                                variant="ghost"
                                size="sm"
                            />
                        </HStack>
                    </HStack>

                    <VStack spacing={4}>
                        {bodyPairs.map((pair, index) => (
                            <HStack key={index} spacing={2} className="w-full">
                                <Input
                                    value={pair.key}
                                    onChange={(e) => updateBodyPair(index, 'key', e.target.value)}
                                    placeholder="Key"
                                    size="md"
                                    variant="outline"
                                    className="rounded-md border-gray-300"
                                />
                                <Input
                                    value={pair.value}
                                    onChange={(e) => updateBodyPair(index, 'value', e.target.value)}
                                    placeholder="Value"
                                    size="md"
                                    variant="outline"
                                    className="rounded-md border-gray-300"
                                />
                                <Switch
                                    isChecked={true}
                                    onChange={(e) => { }}
                                    size="md"
                                    color={'black'}
                                />
                                <Text className="text-sm text-gray-600">Private</Text>
                                <IconButton
                                    aria-label="Delete body parameter"
                                    icon={<DeleteIcon color={'black'} />}
                                    onClick={() => removeBodyPair(index)}
                                    variant="ghost"
                                    size="sm"
                                    colorScheme="red"
                                />
                            </HStack>
                        ))}
                    </VStack>
                </Box> */}
        <Flex alignSelf={'end'} alignItems="center" gap="16px" justifyContent="flex-end">
          {createRequest ? (
            <Button
              className="small-font stepper-next-button"
              isLoading={isLoading}
              onClick={handelNextClicked}
              fontFamily="inter"
              _hover={{ transform: 'scale(1.05)' }}
            >
              Next
            </Button>
          ) : (
            <Button
              className="small-font stepper-next-button"
              isLoading={isLoading}
              onClick={handleFinishClicked}
              fontFamily="inter"
              _hover={{ transform: 'scale(1.05)' }}
            >
              Skip
            </Button>
          )}
        </Flex>
      </VStack>
    </Box>
  );
};

export default RequestBuild;
